/** A revocable lease bound to the currently active Engine generation. */
export interface EngineOperationLease {
    readonly signal: AbortSignal;
    isCurrent(): boolean;
    assertCurrent(): void;
}

/** Raised when an async operation belongs to an Engine that is no longer active. */
export class StaleEngineOperationError extends Error {
    constructor() {
        super("Engine switched during execution");
        this.name = "StaleEngineOperationError";
    }
}

/**
 * Coordinates async work that must not cross an Engine service boundary.
 *
 * Unlike single-flight controllers, multiple execution leases may coexist.
 * invalidate() revokes all of them at once and interrupts abort-aware waiters.
 */
export class EngineOperationController {
    private generation = 0;
    private abortController = new AbortController();

    capture(): EngineOperationLease {
        const generation = this.generation;
        const signal = this.abortController.signal;
        const isCurrent = () =>
            generation === this.generation && !signal.aborted;
        return {
            signal,
            isCurrent,
            assertCurrent: () => {
                if (!isCurrent()) {
                    throw new StaleEngineOperationError();
                }
            },
        };
    }

    invalidate(): void {
        this.generation += 1;
        this.abortController.abort();
        this.abortController = new AbortController();
    }
}

export function isStaleEngineOperationError(
    error: unknown,
): error is StaleEngineOperationError {
    return error instanceof StaleEngineOperationError;
}
